import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Dropdown,
  Input,
  Message,
  Pagination,
  Table,
} from "semantic-ui-react";
import { DateInput } from "semantic-ui-calendar-react";
import moment from "moment";
import toastr from "toastr";
import { getPKORefundChargebackReportApi } from "../../../../../../../apis/reports/pkoRefundChargebackReportsApi";

const ITEMS_PER_PAGE = 20;
const DATE_FORMAT = "DD-MMM-YYYY";
const API_DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";
const TIME_INPUT_WIDTH = "145px";
const TIME_INPUT_STYLE = {
  width: TIME_INPUT_WIDTH,
  minWidth: TIME_INPUT_WIDTH,
  flexShrink: 0,
};
const DATE_INPUT_STYLE = { width: "155px" };
const REPORT_HEADER_STYLE = {
  backgroundColor: "#1a3a5f",
  padding: "15px 20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  width: "100%",
  gap: "16px",
};
const REPORT_TITLE_STYLE = {
  color: "white",
  fontSize: "18px",
  fontWeight: "bold",
};
const FILTER_BAR_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
};
const FILTER_GROUP_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "nowrap",
};
const FILTER_LABEL_STYLE = {
  color: "white",
  whiteSpace: "nowrap",
};
const LABEL_CELL_STYLE = { display: "grid", gap: "4px", lineHeight: "1.25" };
const DEFAULT_LABEL_LEG = "label";

const STATUS_OPTIONS = [
  { key: "all", text: "All", value: "all" },
  { key: "refunded", text: "Refunded", value: "refunded" },
  { key: "chargeback", text: "Chargeback", value: "chargeback" },
];

const LABEL_ID_PRESENCE_OPTIONS = [
  { key: "all", text: "All", value: "all" },
  { key: "with", text: "Has Label ID", value: "with" },
  { key: "without", text: "Missing Label ID", value: "without" },
];

function formatDate(value) {
  if (!value) {
    return "N/A";
  }

  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "N/A";
}

function normalizeRowLabels(row) {
  if (Array.isArray(row.labels)) {
    return row.labels.filter((label) => label?.labelId);
  }

  if (row.labelId) {
    return [{
      leg: null,
      labelId: row.labelId,
      labelCreatedAt: row.labelCreatedAt,
    }];
  }

  return [];
}

function formatLegPrefix(label, shouldShowLeg) {
  if (!shouldShowLeg) {
    return "";
  }

  return `${label.leg || DEFAULT_LABEL_LEG}: `;
}

function renderLabelIdCell(row) {
  const labels = normalizeRowLabels(row);

  if (labels.length === 0) {
    return "N/A";
  }

  const shouldShowLeg = labels.length > 1;
  return (
    <div style={LABEL_CELL_STYLE}>
      {labels.map((label) => (
        <div key={`${label.leg || DEFAULT_LABEL_LEG}-${label.labelId}`}>
          {formatLegPrefix(label, shouldShowLeg)}
          {label.labelId}
        </div>
      ))}
    </div>
  );
}

function renderLabelDateCell(row) {
  const labels = normalizeRowLabels(row);

  if (labels.length === 0) {
    return "N/A";
  }

  const shouldShowLeg = labels.length > 1;
  return (
    <div style={LABEL_CELL_STYLE}>
      {labels.map((label) => (
        <div key={`${label.leg || DEFAULT_LABEL_LEG}-${label.labelId}`}>
          {formatLegPrefix(label, shouldShowLeg)}
          {formatDate(label.labelCreatedAt)}
        </div>
      ))}
    </div>
  );
}

function renderTrackingCell(row) {
  const labels = normalizeRowLabels(row).filter((label) => label.trackingNumber);

  if (labels.length === 0) {
    return "N/A";
  }

  const shouldShowLeg = labels.length > 1;
  return (
    <div style={LABEL_CELL_STYLE}>
      {labels.map((label) => (
        <div key={`${label.leg || DEFAULT_LABEL_LEG}-${label.trackingNumber}`}>
          {formatLegPrefix(label, shouldShowLeg)}
          {label.trackingUrl ? (
            <a href={label.trackingUrl} target="_blank" rel="noopener noreferrer">
              {label.trackingNumber}
            </a>
          ) : label.trackingNumber}
        </div>
      ))}
    </div>
  );
}

function getErrorMessage(error) {
  return error?.response?.data?.error || error?.message || "Failed to load report.";
}

function buildApiDateTimeParams(startDate, startTime, endDate, endTime) {
  const start = moment(`${startDate} ${startTime}`, `${DATE_FORMAT} ${TIME_FORMAT}`);
  const end = moment(`${endDate} ${endTime}`, `${DATE_FORMAT} ${TIME_FORMAT}`);

  return {
    startDate: start.clone().utc().format(API_DATE_FORMAT),
    startTime: start.clone().utc().format(TIME_FORMAT),
    endDate: end.clone().utc().format(API_DATE_FORMAT),
    endTime: end.clone().utc().format(TIME_FORMAT),
  };
}

const PKORefundChargebackReportTable = () => {
  const today = moment().format(DATE_FORMAT);
  const yesterday = moment().subtract(1, "days").format(DATE_FORMAT);

  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(today);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [status, setStatus] = useState("all");
  const [labelIdPresence, setLabelIdPresence] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const params = useMemo(() => ({
    ...buildApiDateTimeParams(startDate, startTime, endDate, endTime),
    status,
    labelIdPresence,
    page: currentPage,
    itemsPerPage: ITEMS_PER_PAGE,
  }), [currentPage, endDate, endTime, labelIdPresence, startDate, startTime, status]);

  const validateDateRange = useCallback(() => {
    const start = moment(`${startDate} ${startTime}`, `${DATE_FORMAT} HH:mm`);
    const end = moment(`${endDate} ${endTime}`, `${DATE_FORMAT} HH:mm`);

    if (!startDate || !endDate || !start.isValid() || !end.isValid()) {
      toastr.error("Select a valid from and to date.", "Date Error");
      return false;
    }

    if (start.isAfter(end)) {
      toastr.error("From date and time cannot be after to date and time.", "Date Range Error");
      return false;
    }

    if (end.diff(start, "days", true) > 90) {
      toastr.error("Date range cannot exceed 90 days.", "Date Range Error");
      return false;
    }

    return true;
  }, [endDate, endTime, startDate, startTime]);

  useEffect(() => {
    if (!validateDateRange()) {
      setReportData([]);
      setTotalPages(1);
      return;
    }

    let didCancel = false;

    async function fetchReport() {
      setIsLoading(true);
      setApiError(null);

      try {
        const response = await getPKORefundChargebackReportApi(params);
        if (didCancel) {
          return;
        }

        setReportData(response?.data?.data || []);
        setTotalPages(response?.data?.pagination?.totalPages || 1);
      } catch (error) {
        if (didCancel) {
          return;
        }

        setApiError(getErrorMessage(error));
        setReportData([]);
        setTotalPages(1);
      } finally {
        if (!didCancel) {
          setIsLoading(false);
        }
      }
    }

    fetchReport();

    return () => {
      didCancel = true;
    };
  }, [params, validateDateRange]);

  const handleFilterChange = useCallback((setter) => (event, data) => {
    setter(data?.value ?? event.target.value);
    setCurrentPage(1);
  }, []);

  return (
    <div>
      <div
        style={REPORT_HEADER_STYLE}
      >
        <div style={REPORT_TITLE_STYLE}>
          PKO Refund and Chargeback Report
        </div>

        <div style={FILTER_BAR_STYLE}>
          <div style={FILTER_GROUP_STYLE}>
            <span style={FILTER_LABEL_STYLE}>From:</span>
            <DateInput
              name="startDate"
              placeholder="From Date"
              value={startDate}
              iconPosition="left"
              dateFormat={DATE_FORMAT}
              animation="none"
              duration={0}
              onChange={handleFilterChange(setStartDate)}
              clearable
              style={DATE_INPUT_STYLE}
              popupPosition="top left"
              maxDate={today}
            />
            <Input
              type="time"
              value={startTime}
              onChange={handleFilterChange(setStartTime)}
              onInput={handleFilterChange(setStartTime)}
              style={TIME_INPUT_STYLE}
            />
          </div>

          <div style={FILTER_GROUP_STYLE}>
            <span style={FILTER_LABEL_STYLE}>To:</span>
            <DateInput
              name="endDate"
              placeholder="To Date"
              value={endDate}
              iconPosition="left"
              dateFormat={DATE_FORMAT}
              animation="none"
              duration={0}
              onChange={handleFilterChange(setEndDate)}
              clearable
              style={DATE_INPUT_STYLE}
              popupPosition="top left"
              maxDate={today}
            />
            <Input
              type="time"
              value={endTime}
              onChange={handleFilterChange(setEndTime)}
              onInput={handleFilterChange(setEndTime)}
              style={TIME_INPUT_STYLE}
            />
          </div>

          <div style={FILTER_GROUP_STYLE}>
            <span style={FILTER_LABEL_STYLE}>Status:</span>
            <Dropdown
              selection
              options={STATUS_OPTIONS}
              value={status}
              onChange={handleFilterChange(setStatus)}
              style={{ minWidth: "145px" }}
            />
          </div>

          <div style={FILTER_GROUP_STYLE}>
            <span style={FILTER_LABEL_STYLE}>Label ID:</span>
            <Dropdown
              selection
              options={LABEL_ID_PRESENCE_OPTIONS}
              value={labelIdPresence}
              onChange={handleFilterChange(setLabelIdPresence)}
              style={{ minWidth: "155px" }}
            />
          </div>
        </div>
      </div>

      {isLoading && <Message info header="Loading report data..." />}
      {apiError && <Message error header="Failed to load report" content={apiError} />}

      {!isLoading && !apiError && reportData.length === 0 && (
        <Message
          warning
          header="No Data"
          content="No records found matching your filters."
        />
      )}

      {!isLoading && !apiError && reportData.length > 0 && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Applicant name</Table.HeaderCell>
              <Table.HeaderCell>UCC ID</Table.HeaderCell>
              <Table.HeaderCell>Application status</Table.HeaderCell>
              <Table.HeaderCell>Status changed at</Table.HeaderCell>
              <Table.HeaderCell>Application creation date</Table.HeaderCell>
              <Table.HeaderCell>Label ID</Table.HeaderCell>
              <Table.HeaderCell>Tracking ID</Table.HeaderCell>
              <Table.HeaderCell>Label creation date</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {reportData.map((row) => (
              <Table.Row key={`${row.applicantId}-${row.statusChangedAt}`}>
                <Table.Cell>{row.applicantName || "N/A"}</Table.Cell>
                <Table.Cell>
                  <a href={row.uccPath} target="_blank" rel="noopener noreferrer">
                    {row.applicantId}
                  </a>
                </Table.Cell>
                <Table.Cell>{row.applicationStatus || "N/A"}</Table.Cell>
                <Table.Cell>{formatDate(row.statusChangedAt)}</Table.Cell>
                <Table.Cell>{formatDate(row.applicationCreatedAt)}</Table.Cell>
                <Table.Cell>{renderLabelIdCell(row)}</Table.Cell>
                <Table.Cell>{renderTrackingCell(row)}</Table.Cell>
                <Table.Cell>{renderLabelDateCell(row)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {!isLoading && !apiError && (totalPages > 1 || reportData.length > 0) && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "20px 0" }}>
          <Pagination
            activePage={currentPage}
            onPageChange={(event, { activePage }) => setCurrentPage(activePage)}
            totalPages={Math.max(1, totalPages)}
            ellipsisItem={{ content: "...", icon: true }}
            firstItem={{ content: "\u00ab", icon: true }}
            lastItem={{ content: "\u00bb", icon: true }}
            prevItem={{ content: "\u2039", icon: true }}
            nextItem={{ content: "\u203a", icon: true }}
          />
        </div>
      )}
    </div>
  );
};

export default PKORefundChargebackReportTable;
